import { useCallback } from 'react';
import { logger } from '@ui/utils/logger';
import { useUser } from './useUser';

export enum AuditAction {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    DELETE = 'DELETE',
    VIEW = 'VIEW',
    EXPORT = 'EXPORT',
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    DOWNLOAD = 'DOWNLOAD',
    UPLOAD = 'UPLOAD',
    EXECUTE = 'EXECUTE',
}

export enum AuditEntityType {
    CURATION_JOB = 'CURATION_JOB',
    ONTOLOGY = 'ONTOLOGY',
    PATIENT = 'PATIENT',
    COHORT = 'COHORT',
    CALCULATION = 'CALCULATION',
    DATABASE = 'DATABASE',
    USER = 'USER',
    FILE = 'FILE',
    RESULT = 'RESULT',
}

export enum AuditStatus {
    SUCCESS = 'success',
    FAILURE = 'failure',
}

export interface AuditLogPayload {
    action: AuditAction;
    entity_type: AuditEntityType;
    entity_id?: string;
    entity_name?: string;
    description?: string;
    metadata?: Record<string, any>;
    status?: AuditStatus;
    error_message?: string;
    duration_ms?: number;
}

export function useAudit() {
    const user = useUser();

    /**
     * Send audit log using Beacon API (fire-and-forget)
     * Falls back to fetch with keepalive if Beacon is not available
     */
    const logAudit = useCallback(
        (payload: AuditLogPayload): boolean => {
            // Only log if user is authenticated
            if (!user.isAuthenticated || !user.id) {
                logger.warn('Cannot log audit: user not authenticated');
                return false;
            }

            const auditData = {
                user_id: user.id,
                user_email: user.email,
                user_name: user.name || `${user.firstName} ${user.lastName}`.trim(),
                action: payload.action,
                entity_type: payload.entity_type,
                entity_id: payload.entity_id,
                entity_name: payload.entity_name,
                description: payload.description,
                metadata: payload.metadata,
                status: payload.status || AuditStatus.SUCCESS,
                error_message: payload.error_message,
                duration_ms: payload.duration_ms,
            };

            try {
                const beaconUrl = '/api/audit/beacon';
                const blob = new Blob([JSON.stringify(auditData)], {
                    type: 'application/json',
                });

                // Try Beacon API first (preferred for fire-and-forget)
                if (navigator.sendBeacon) {
                    const success = navigator.sendBeacon(beaconUrl, blob);
                    if (success) {
                        return true;
                    }
                    // If beacon fails, fall through to fetch
                    logger.warn('Beacon API failed, falling back to fetch');
                }

                // Fallback to fetch with keepalive (works during page unload)
                fetch(beaconUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(auditData),
                    keepalive: true, // Ensures request completes even if page is closing
                }).catch(error => {
                    // Silently fail - audit should never disrupt user experience
                    logger.error('Failed to log audit', error);
                });

                return true;
            } catch (error) {
                // Silently fail - audit should never disrupt user experience
                logger.error('Failed to log audit', error);
                return false;
            }
        },
        [user]
    );

    /**
     * Convenience method for logging successful actions
     */
    const logSuccess = useCallback(
        (
            action: AuditAction,
            entityType: AuditEntityType,
            options?: {
                entity_id?: string;
                entity_name?: string;
                description?: string;
                metadata?: Record<string, any>;
                duration_ms?: number;
            }
        ): boolean => {
            return logAudit({
                action,
                entity_type: entityType,
                status: AuditStatus.SUCCESS,
                ...options,
            });
        },
        [logAudit]
    );

    /**
     * Convenience method for logging failed actions
     */
    const logFailure = useCallback(
        (
            action: AuditAction,
            entityType: AuditEntityType,
            errorMessage: string,
            options?: {
                entity_id?: string;
                entity_name?: string;
                metadata?: Record<string, any>;
                duration_ms?: number;
            }
        ): boolean => {
            return logAudit({
                action,
                entity_type: entityType,
                status: AuditStatus.FAILURE,
                error_message: errorMessage,
                description: options?.entity_name
                    ? `Failed to ${action.toLowerCase()} ${options.entity_name}`
                    : undefined,
                ...options,
            });
        },
        [logAudit]
    );

    /**
     * Log an action with automatic timing
     * Returns a function to call when the action completes
     *
     * @example
     * ```typescript
     * const completeAudit = startTimedAudit(AuditAction.EXPORT, AuditEntityType.RESULT, {
     *   entity_id: result.id,
     * });
     *
     * try {
     *   await exportData();
     *   completeAudit(); // Logs success with duration
     * } catch (error) {
     *   completeAudit(error.message); // Logs failure with duration
     * }
     * ```
     */
    const startTimedAudit = useCallback(
        (
            action: AuditAction,
            entityType: AuditEntityType,
            options?: {
                entity_id?: string;
                entity_name?: string;
                description?: string;
                metadata?: Record<string, any>;
            }
        ): ((errorMessage?: string) => boolean) => {
            const startTime = Date.now();

            return (errorMessage?: string) => {
                const duration = Date.now() - startTime;

                if (errorMessage) {
                    return logFailure(action, entityType, errorMessage, {
                        ...options,
                        duration_ms: duration,
                    });
                }

                return logSuccess(action, entityType, {
                    ...options,
                    duration_ms: duration,
                });
            };
        },
        [logSuccess, logFailure]
    );

    return {
        logAudit,
        logSuccess,
        logFailure,
        startTimedAudit,
    };
}
